import { z } from "zod";
import { journalWebdocStatusSchema } from "./journal-provenance";
import {
  JOURNAL_SECTION_HEADINGS,
  JOURNAL_SECTION_KEYS,
  meetingJournalSectionSchema,
  updateMeetingJournalSchema,
  type JournalLayout,
  type JournalSectionKey,
} from "./meeting-journal";
import type { MeetingPrescriptionOrder } from "./prescription-cycle";

// TES-1034 S7 (Emelie, points 1-2) — the prescription transfer review as a
// journal flow, built on the first care meeting's model without the video.
// Kirill writes under his headings (the care meeting's seven boxes), the
// boxes are prefilled from F-01, F-02B and F-07 with their source, and the
// order he approves is written under the generated "Åtgärd och
// behandlingsplan" (TES-1328). Every decision on the review (approval, komplettering, blood
// test, cannot transfer, meeting) is sent with the confirmation that the
// compiled note is entered in Webdoc, and the API stores that exact text as
// the note's final version with his receipt (Ö-284, assumption (a)).

/** The transfer review's note: the care meeting's layout. */
export const TRANSFER_JOURNAL_LAYOUT: JournalLayout<JournalSectionKey> = {
  keys: JOURNAL_SECTION_KEYS,
  headings: JOURNAL_SECTION_HEADINGS,
  // TES-1058 — the decision's lines, as in the care meeting's note.
  assessmentSection: "bedomning",
  // TES-1328 — the order, the decision's plan lines and the monthly
  // follow-up are generated under the locked "Åtgärd och behandlingsplan";
  // there is no "Ordination" heading any more.
  generatedSection: "atgard_behandlingsplan",
};

export const transferReviewJournalSchema = z.object({
  transferReviewId: z.string().uuid(),
  sections: z.array(meetingJournalSectionSchema),
  webdoc: journalWebdocStatusSchema.optional(),
});
export type TransferReviewJournal = z.infer<typeof transferReviewJournalSchema>;

// PUT …/transfer-review/journal — the boxes the doctor changed.
export const updateTransferReviewJournalSchema = updateMeetingJournalSchema;
export type UpdateTransferReviewJournal = z.infer<
  typeof updateTransferReviewJournalSchema
>;

/**
 * The order the note's planned medication is written from (TES-1328): the
 * approval's order, or null for a decision that prescribes nothing.
 * TES-1242 — also the plan saved with a komplettering, which the note writes
 * as the conditional planned treatment while the kompletteringar are
 * pending; strength and plan ride along when given.
 */
export function transferJournalOrder(
  approval:
    | {
        medication?: string | null;
        dose?: string | null;
        frequency?: string | null;
        duration?: string | null;
        strength?: string | null;
        plan?: string | null;
      }
    | null
    | undefined,
): MeetingPrescriptionOrder | null {
  if (!approval) return null;
  const strength = (approval.strength ?? "").trim();
  const plan = (approval.plan ?? "").trim();
  return {
    medication: (approval.medication ?? "").trim(),
    dose: (approval.dose ?? "").trim(),
    frequency: (approval.frequency ?? "").trim(),
    duration: (approval.duration ?? "").trim(),
    ...(strength ? { strength } : {}),
    ...(plan ? { plan } : {}),
  };
}
